/**
 * Lightweight client-side router — no external dependencies.
 * Replaces react-router-dom to avoid the broken parent node_modules.
 *
 * Usage:
 *   useRoute()            → { path }
 *   navigate('/login')    → push to history
 *   <Link to="/signup">   → <a> that calls navigate
 */
import { useState, useEffect, createContext, useContext } from "react"

// ─── Context ──────────────────────────────────────────────────────────────────
const RouterCtx = createContext({ path: "/" })

// ─── Provider ─────────────────────────────────────────────────────────────────
export function Router({ children }) {
  const [path, setPath] = useState(() => window.location.pathname)

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [])

  // Expose navigate on window so it can be called outside React tree if needed
  useEffect(() => {
    window.__navigate = (to) => {
      window.history.pushState(null, "", to)
      setPath(to)
    }
  }, [])

  return <RouterCtx.Provider value={{ path, setPath }}>{children}</RouterCtx.Provider>
}

// ─── Hook: current path ───────────────────────────────────────────────────────
export function useRoute() {
  return useContext(RouterCtx)
}

// ─── navigate helper ──────────────────────────────────────────────────────────
export function navigate(to, options = {}) {
  window.history.pushState(options.state || null, "", to)
  // Dispatch a popstate-like update via a custom event
  window.dispatchEvent(new PopStateEvent("popstate"))
}

// ─── useNavigate hook (matches react-router-dom API) ─────────────────────────
export function useNavigate() {
  return navigate
}

// ─── useLocation hook (matches react-router-dom API) ─────────────────────────
export function useLocation() {
  const { path } = useRoute()
  return { pathname: path, state: window.history.state }
}

// ─── <Link> component ─────────────────────────────────────────────────────────
export function Link({ to, children, className, id, onClick, ...rest }) {
  const handleClick = (e) => {
    e.preventDefault()
    if (onClick) onClick(e)
    navigate(to)
  }
  return (
    <a href={to} onClick={handleClick} className={className} id={id} {...rest}>
      {children}
    </a>
  )
}

// ─── <Routes> / <Route> ───────────────────────────────────────────────────────
export function Routes({ children }) {
  const { path } = useRoute()
  const routes = Array.isArray(children) ? children : [children]
  const match = routes.find((r) => r?.props?.path === path || r?.props?.path === "*")
  return match ? match.props.element : null
}

export function Route({ path, element }) {
  // Route is consumed by Routes — this component itself renders nothing
  return null
}
